import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, ShieldCheck, AlertTriangle, MoreHorizontal, Trash2, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface VendorRiskAssessment {
  id: string
  vendorName: string
  domain: string | null
  overallScore: number
  riskLevel: string
  securityScore: number | null
  complianceScore: number | null
  financialScore: number | null
  operationalScore: number | null
  lastAssessedAt: string
  createdAt: string
}

function riskLevelBadge(level: string) {
  switch (level) {
    case 'critical': return <Badge variant="destructive">Critical</Badge>
    case 'high': return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">High</Badge>
    case 'medium': return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Medium</Badge>
    case 'low': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Low</Badge>
    default: return <Badge variant="secondary">{level}</Badge>
  }
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-500'
  if (score >= 60) return 'text-yellow-500'
  if (score >= 40) return 'text-orange-500'
  return 'text-red-500'
}

function AssessVendorDialog() {
  const [open, setOpen] = useState(false)
  const [vendorName, setVendorName] = useState('')
  const [domain, setDomain] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/vendor-risk', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-risk'] })
      toast({ title: 'Assessment started', description: 'Vendor risk assessment initiated' })
      setOpen(false)
      setVendorName('')
      setDomain('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to start assessment', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Assess Vendor</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assess Vendor</DialogTitle>
          <DialogDescription>Start a new vendor risk assessment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Vendor Name</Label>
            <Input value={vendorName} onChange={(e) => setVendorName(e.target.value)} placeholder="Acme Corp" />
          </div>
          <div className="space-y-2">
            <Label>Domain</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acmecorp.com" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({ vendorName, domain: domain || undefined })}
            disabled={!vendorName || createMutation.isPending}
          >
            {createMutation.isPending ? 'Starting...' : 'Start Assessment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function VendorRiskPage() {
  const [search, setSearch] = useState('')
  const [riskFilter, setRiskFilter] = useState<string>('all')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['vendor-risk', riskFilter],
    queryFn: () => apiClient.get<{ vendors: VendorRiskAssessment[]; pagination: unknown }>('/vendor-risk', {
      ...(riskFilter !== 'all' && { riskLevel: riskFilter }),
    }),
  })

  const reassessMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/vendor-risk/${id}/reassess`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-risk'] })
      toast({ title: 'Re-assessment started', description: 'Vendor is being re-assessed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to start re-assessment', variant: 'destructive' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/vendor-risk/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-risk'] })
      toast({ title: 'Deleted', description: 'Vendor assessment removed' })
    },
  })

  const vendors = data?.data?.vendors || []
  const filtered = vendors.filter((v) => v.vendorName.toLowerCase().includes(search.toLowerCase()))

  const highRiskCount = vendors.filter((v) => v.riskLevel === 'high' || v.riskLevel === 'critical').length
  const avgScore = vendors.length > 0
    ? Math.round(vendors.reduce((sum, v) => sum + v.overallScore, 0) / vendors.length)
    : 0
  const dueForReview = vendors.filter((v) => {
    const daysSince = Math.ceil((Date.now() - new Date(v.lastAssessedAt).getTime()) / (1000 * 60 * 60 * 24))
    return daysSince > 90
  }).length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vendor Risk Assessment</h1>
          <p className="text-muted-foreground">Assess and monitor third-party vendor risk posture</p>
        </div>
        <AssessVendorDialog />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-muted-foreground" /><p className="text-xs text-muted-foreground">Total Vendors</p></div><div className="text-2xl font-bold mt-1">{vendors.length}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-orange-500" /><p className="text-xs text-muted-foreground">High Risk</p></div><div className="text-2xl font-bold text-orange-500 mt-1">{highRiskCount}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-500" /><p className="text-xs text-muted-foreground">Average Score</p></div><div className={`text-2xl font-bold mt-1 ${scoreColor(avgScore)}`}>{avgScore}/100</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><RefreshCw className="h-4 w-4 text-yellow-500" /><p className="text-xs text-muted-foreground">Due for Review</p></div><div className="text-2xl font-bold text-yellow-500 mt-1">{dueForReview}</div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search vendors..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={riskFilter} onValueChange={setRiskFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Risk Level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Card key={i}><CardHeader><Skeleton className="h-5 w-32" /></CardHeader><CardContent><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3 mt-2" /></CardContent></Card>)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ShieldCheck className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No vendor assessments</p>
            <p className="text-sm text-muted-foreground">Start by assessing a vendor</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((vendor) => (
            <Card key={vendor.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                <div className="space-y-1">
                  <CardTitle className="text-base font-medium">{vendor.vendorName}</CardTitle>
                  {vendor.domain && (
                    <p className="text-sm text-muted-foreground">{vendor.domain}</p>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => reassessMutation.mutate(vendor.id)}>
                      <RefreshCw className="mr-2 h-4 w-4" /> Re-assess
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(vendor.id)}>
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`text-3xl font-bold ${scoreColor(vendor.overallScore)}`}>
                    {vendor.overallScore}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">/ 100</p>
                    {riskLevelBadge(vendor.riskLevel)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {vendor.securityScore !== null && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Security: </span>
                      <span className={`font-medium ${scoreColor(vendor.securityScore)}`}>{vendor.securityScore}</span>
                    </div>
                  )}
                  {vendor.complianceScore !== null && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Compliance: </span>
                      <span className={`font-medium ${scoreColor(vendor.complianceScore)}`}>{vendor.complianceScore}</span>
                    </div>
                  )}
                  {vendor.financialScore !== null && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Financial: </span>
                      <span className={`font-medium ${scoreColor(vendor.financialScore)}`}>{vendor.financialScore}</span>
                    </div>
                  )}
                  {vendor.operationalScore !== null && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Operational: </span>
                      <span className={`font-medium ${scoreColor(vendor.operationalScore)}`}>{vendor.operationalScore}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Last assessed: {new Date(vendor.lastAssessedAt).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
